import React, { useState, useEffect, ChangeEvent } from 'react';
import { Box, Button, Chip, Container, Dialog, DialogContent, DialogTitle, Divider, IconButton, Paper, Stack, Typography, alpha } from '@mui/material';
import { deleteDoc, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { updateUmrahPaperRequestStatus } from '../../../utils/updateUmrahPaperRequest';
import umrahPaperRequestConverter from '../../../converters/umrahPaperRequestConverter';
import { RunCircleRounded, MopedRounded, CheckCircleRounded, DeleteRounded, EditRounded, OpenInNewRounded } from '@mui/icons-material';
import { FileBox } from '../FileBox';
import { DateTime } from 'luxon';
import { type } from '@testing-library/user-event/dist/type';
import { storage } from 'firebase-admin';
import { uploadString, ref, getStorage } from 'firebase/storage';
import { UmrahPaperRequestForm } from './UmrahPaperRequestForm';
import { updateUniformRequestStatus } from '../../../utils/updateUniformRequest';
import { UmrahPaperRequest, UmrahPaperRequestStatus } from '../../../types/UmrahPaperRequest';
import { UmrahPaperRequestInfoBox } from './UmrahPaperRequestInfoBox';
import { SecureComponent } from '../../../security/SecureComponent';
import { hasPermission } from '../../../security/Permission';
import { useAppSelector } from '../../../store';

export const UmrahPaperRequestPage = () => {
  const storage =  getStorage();
  const db = getFirestore();
  const navigate = useNavigate();
  const authState = useAppSelector(s => s.auth);

  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { state } = useLocation();

  const [umrahPaperRequest, setUmrahPaperRequest] = useState<UmrahPaperRequest>((state as any)?.umrahPaperRequest ?? undefined);
  const [disableDelete, setDisableDelete] = useState(false);

  const [showForm, setShowForm] = useState(false);
  
  const updateStatus = (status: UmrahPaperRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateUmrahPaperRequestStatus(umrahPaperRequest, status).then(() => {
        getDoc(doc(db, `Employees/${umrahPaperRequest.idNumber}/UmrahPaperRequests/${id}`).withConverter(umrahPaperRequestConverter)).then(docSnapshot => {
          if (docSnapshot.exists()) {
            setUmrahPaperRequest(docSnapshot.data());
          }
        });
        enqueueSnackbar(`Umrah paper request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleDeleteOnClick = () => {
    if (window.confirm('Are you sure you want to delete this umrah paper request?')) {
      setDisableDelete(true);
      deleteDoc(doc(db, `Employees/${umrahPaperRequest.idNumber}/UmrahPaperRequests/${umrahPaperRequest.id}`))
        .then(() => {
          enqueueSnackbar('Umrah papers request has been deleted.', { variant: 'success' });
        })
        .finally(() => {
          setDisableDelete(false);
          setTimeout(() => navigate(-1), 250);
      });
    }
  };

  const openForm = () => setShowForm(true);
  const closeForm = () => setShowForm(false);
  const handleSetProcessingOnClick = () => updateStatus('Processing');
  const handleSetAvailableOnClick = () => updateStatus('Available');
  const handleSetCompletedOnClick = () => updateStatus('Completed');
  
  const handleFormOnSave = (m: UmrahPaperRequest) => {
    setUmrahPaperRequest(m);
    setShowForm(false);
  };

  const upload = async (fileName: string, data: string) => {
    const idNumber = umrahPaperRequest.idNumber;
    const id = umrahPaperRequest.id;
    const dateString = DateTime.utc().toFormat("yyyyMMdd_hhmmss");
    const file = 'File_' + id + '_' + dateString + '_' + fileName;

    const dest = `${idNumber}/UmrahPaperRequests/${file}`;
    return await uploadString(ref(storage, dest),  data, 'data_url')
      .then(async () => {
        const url = "https://firebasestorage.googleapis.com/v0/b/nadafa-ems.appspot.com/o/" + encodeURIComponent(dest) + "?alt=media";
        await setDoc(doc(db, `Employees/${idNumber}/UmrahPaperRequests/${id}`), { file: url }, { merge: true });
        return url;
      })
      .then((url) => {
        // Update selected vacation
        setUmrahPaperRequest({ ...umrahPaperRequest, file: url });
        enqueueSnackbar('File has been uploaded!', { variant: 'success' });
        return true;
      })
      .catch(r => {
        enqueueSnackbar('An error has occured. ' + r, { variant: 'error' });
        return false;
      });
  };

  useEffect(() => {
    if (!state || !(state as any)?.uniformRequest) {
      getDoc(doc(db, `UmrahPaperRequests/${id}`).withConverter(umrahPaperRequestConverter)).then(docSnapshot => {
        if (docSnapshot.exists()) {
          setUmrahPaperRequest(docSnapshot.data());
        }
      });
    }
  }, []);

  if (!umrahPaperRequest) {
    return (
      <Typography variant="caption">Please wait...</Typography>
    );
  }
  
  return (
    <>
      <Container disableGutters>
        <Stack spacing={3} direction="row" alignItems="center" mb={2}>
          <Chip label={umrahPaperRequest.idNumber} />
          <Typography variant="h6">{umrahPaperRequest.name}</Typography>
          <SecureComponent permissions={['update.umrahRequests', 'delete.umrahRequests']}>
            <Stack direction='row' spacing={1} sx={{ display: 'flex', flexGrow: 1, justifyContent: 'right' }}>
              <SecureComponent permissions={['update.umrahRequests']}>
                <Button startIcon={<EditRounded />} color="warning" onClick={openForm}>EDIT</Button>
              </SecureComponent>
              <SecureComponent permissions={['delete.umrahRequests']}>
                <Button startIcon={<DeleteRounded />} color="error" disabled={disableDelete} onClick={handleDeleteOnClick}>DELETE</Button>
              </SecureComponent>
            </Stack>
          </SecureComponent>
        </Stack>
        <Paper>
          <UmrahPaperRequestInfoBox umrahPaperRequest={umrahPaperRequest} />
        </Paper>
        <SecureComponent permissions={['update.umrahRequests']}>
          <Stack mt={1} spacing={1} direction="row" justifyContent="right">
            <Button 
              color="warning" 
              startIcon={<RunCircleRounded />} 
              disabled={umrahPaperRequest.status !== 'New'}
              onClick={handleSetProcessingOnClick}
            >
              Processing
            </Button>
            <Button 
              color="secondary" 
              startIcon={<MopedRounded />}
              disabled={umrahPaperRequest.status !== 'New' && umrahPaperRequest.status !== 'Processing'}
              onClick={handleSetAvailableOnClick}
            >
              For Pickup
            </Button>
            <Button 
              color="success" 
              startIcon={<CheckCircleRounded />} 
              disabled={umrahPaperRequest.status === 'Completed' || umrahPaperRequest.status === 'Cancelled'}
              onClick={handleSetCompletedOnClick}
            >
              Completed
            </Button>
          </Stack>
        </SecureComponent>
        {umrahPaperRequest.status !== 'New' && (
          <Stack mt={3} spacing={1}>
            <FileBox label="File" file={umrahPaperRequest.file} onUpload={upload} canUpload={hasPermission(authState, [], ['update.umrahRequests'])} />
          </Stack>
        )}
      </Container>
      <SecureComponent permissions={['create.umrahRequests']}>
        <Dialog open={showForm} onClose={closeForm} fullWidth>
          <DialogTitle>Update Umrah Paper Request</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 0 }}>
            {umrahPaperRequest && (
              <UmrahPaperRequestForm umrahPaperRequest={umrahPaperRequest} onClosed={closeForm} onSaved={handleFormOnSave} />
            )}
          </DialogContent>
        </Dialog>
      </SecureComponent>
    </>
  );
};
